// src/components/ProjectsCardShow.jsx
import React, { useEffect, useState, useCallback } from "react";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import "../assets/css/projectList.css";

/* ───────────────────────────────────────── modal helper ─── */
function IframeModal({ project, onClose }) {
  // close on ESC
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  if (!project) return null;

  return (
    <div className="iframe-modal" onClick={onClose}>
      <div className="iframe-modal__inner" onClick={(e) => e.stopPropagation()}>
        <button className="iframe-modal__close" onClick={onClose}>
          &times;
        </button>
        <iframe
          src={project.websiteUrl}
          title={project.title}
          sandbox="allow-scripts allow-same-origin allow-forms"
          allowFullScreen
        />
      </div>
    </div>
  );
}

/* ───────────────────────────────────────── main component ─── */
export default function ProjectsCardShow() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragStartIndex, setDragStartIndex] = useState(0);
  const [modalProject, setModalProject] = useState(null); // NEW ✔
  const [visibleProjects, setVisibleProjects] = useState(4); // Track visible projects count

  /* ── get visible projects count based on screen size ───────────────────────*/
  const getVisibleProjectsCount = useCallback(() => {
    const width = window.innerWidth;
    if (width <= 768) return 1;      // Mobile: 1 project
    if (width <= 1024) return 2;     // Tablet: 2 projects  
    if (width <= 1250) return 3;     // Small desktop: 3 projects
    return 4;                        // Large desktop: 4 projects
  }, []);

  /* ── update visible projects count on resize ───────────────────────────────*/
  useEffect(() => {
    const updateVisibleCount = () => {
      setVisibleProjects(getVisibleProjectsCount());
    };

    updateVisibleCount(); // Initial call
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, [getVisibleProjectsCount]);

  /* ── fetch from Firestore ─────────────────────────────────*/
  useEffect(() => {
    async function fetchProjects() {
      try {
        const q = query(collection(db, "projects"), orderBy("title"));
        const snapshot = await getDocs(q);
        const items = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            imageUrl: data.imageUrl?.startsWith("/")
              ? data.imageUrl
              : `/${data.imageUrl}`,
            websiteUrl: data.websiteUrl || data.websiteUrl1 || "", // ← grab the URL
          };
        });
        setProjects(items);
      } catch (e) {
        console.error("Firestore error – using fallback data", e);
        setProjects([
          {
            id: "1",
            title: "Fallback 1",
            category: "Demo",
            imageUrl: "/img/projects/1-480x880.jpg",
            websiteUrl: "https://example.com",
          },
          {
            id: "2",
            title: "Fallback 2",
            category: "Demo",
            imageUrl: "/img/projects/2-480x880.jpg",
            websiteUrl: "https://example.com",
          },
          // …
        ]);
      } finally {
        setLoading(false);
      }
    }
    fetchProjects();
  }, []);

  /* ── magnificPopup init (unchanged) ───────────────────────*/
  useEffect(() => {
    if (!loading && window.jQuery) {
      window.jQuery(".js-projects-gallery").magnificPopup({
        delegate: "a",
        type: "image",
        closeOnContentClick: true,
        mainClass: "mfp-fade",
        gallery: { enabled: true, navigateByImgClick: true, preload: [0, 1] },
        image: {
          tError: '<a href="%url%">The image #%curr%</a> could not be loaded.',
        },
      });
    }
  }, [loading, projects]);

  /* ── carousel helpers (updated for responsive behavior) ─────────────────────────*/
  const handlePrev = () => {
    if (!isAnimating && currentIndex > 0) {
      setIsAnimating(true);
      setCurrentIndex((i) => i - 1);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };
  
  const handleNext = () => {
    if (!isAnimating && currentIndex < projects.length - visibleProjects) {
      setIsAnimating(true);
      setCurrentIndex((i) => i + 1);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const handleDragStart = (e) => {
    const x = e.type.includes("mouse") ? e.pageX : e.touches[0].pageX;
    setDragStart(x);
    setDragStartIndex(currentIndex);
    setIsAnimating(false);
  };
  
  const handleDragMove = (e) => {
    if (!dragStart) return;
    const x = e.type.includes("mouse") ? e.pageX : e.touches[0].pageX;
    const diff = dragStart - x;
    const threshold = 50;
    if (Math.abs(diff) > threshold) {
      if (diff > 0 && currentIndex < projects.length - visibleProjects)
        setCurrentIndex(dragStartIndex + 1);
      if (diff < 0 && currentIndex > 0) setCurrentIndex(dragStartIndex - 1);
      setDragStart(0);
    }
  };
  
  const handleDragEnd = () => setDragStart(0);

  /* ── open modal on click  ─────────────────────────────────*/
  const openModal = useCallback((project) => {
    if (!project.websiteUrl) return; // nothing to open
    setModalProject(project);
  }, []);

  if (loading) return <div className="section-content">Loading projects…</div>;

  return (
    <>
      <div className="section-content">
        <div className="projects-carousel js-projects-carousel js-projects-gallery">
          <div
            className="carousel-container"
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            {projects.map((p) => (
              <div
                key={p.id}
                className="project project-light"
                style={{
                  transform: `translateX(${-currentIndex * 100}%)`,
                  transition: dragStart ? "none" : "transform 0.5s ease",
                }}
                onClick={() => openModal(p)} /* NEW ✔ */
              >
                <figure>
                  <img
                    src={p.imageUrl}
                    alt={p.title}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/img/projects/1-480x880.jpg";
                    }}
                  />
                  <figcaption>
                    <h3 className="project-title">{p.title}</h3>
                    <h4 className="project-category">{p.category}</h4>
                    <div className="project-zoom" />
                  </figcaption>
                </figure>
              </div>
            ))}
          </div>
          <div className="carousel-nav">
            <button
              className="prev"
              onClick={handlePrev}
              disabled={currentIndex === 0 || isAnimating}
            >
              ← PREV
            </button>
            <button
              className="next"
              onClick={handleNext}
              disabled={currentIndex >= projects.length - visibleProjects || isAnimating}
            >
              NEXT →
            </button>
          </div>
        </div>
      </div>

      {/* Modal renders here */}
      <IframeModal
        project={modalProject}
        onClose={() => setModalProject(null)}
      />
    </>
  );
}

/* ─────────────────────────────── CSS (add somewhere) ──────
.iframe-modal{
  position:fixed; inset:0; background:rgba(0,0,0,.8); z-index:2000;
  display:flex; align-items:center; justify-content:center;
}
.iframe-modal__inner{position:relative; width:90%; height:90%;}
.iframe-modal__inner iframe{border:0; width:100%; height:100%; border-radius:4px;}
.iframe-modal__close{
  position:absolute; top:-40px; right:0;
  background:none; border:0; color:#fff; font-size:2rem; cursor:pointer;
}
----------------------------------------------------------------*/
